import { useState, useEffect } from "react";
import { useAuth } from "../../App";
import Sidebar from "../../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "../../components/ui/alert";
import { toast } from "sonner";
import {
  FolderOpen,
  Plus,
  MoreVertical,
  Edit,
  Trash2,
  Users,
  BookOpen,
  Calendar,
  Palette,
  Eye,
  EyeOff,
  Wand2,
  AlertCircle,
  CheckCircle,
} from "lucide-react";

import { API } from "../../utils/api";

const AdminPrograms = () => {
  const { token } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    description_long: "",
    duration_months: 3,
    delay_between_modules_days: 12,
    price: "",
    currency: "EUR",
    color: "#FF6B35",
    has_pcm_personalization: true,
    welcome_message: "",
  });

  useEffect(() => {
    fetchPrograms();
  }, [token]);

  const fetchPrograms = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/admin/programs`, {
        headers,
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setPrograms(data);
      }
    } catch (error) {
      console.error("Error fetching programs:", error);
      toast.error("Erreur lors du chargement des programmes");
    } finally {
      setLoading(false);
    }
  };

  // Migration function to create FLOW program and assign modules/users
  const runMigration = async () => {
    if (!confirm("Lancer la migration ? Cela va créer le programme FLOW et assigner tous les modules et utilisateurs existants.")) {
      return;
    }
    
    setMigrationLoading(true);
    setMigrationResult(null);
    
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const response = await fetch(`${API}/admin/migrate/create-flow-program`, {
        method: "POST",
        headers,
        credentials: "include",
      });

      const result = await response.json();
      setMigrationResult(result);
      
      if (result.success) {
        toast.success("Migration terminée avec succès !");
        fetchPrograms(); // Refresh the list
      } else {
        toast.error("Erreur lors de la migration");
      }
    } catch (error) {
      console.error("Migration error:", error);
      toast.error("Erreur lors de la migration");
      setMigrationResult({ success: false, message: error.message });
    } finally {
      setMigrationLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  const handleNameChange = (name) => {
    setFormData({
      ...formData,
      name,
      slug: generateSlug(name),
    });
  };

  const resetForm = () => {
    setFormData({
      name: "",
      slug: "",
      description: "",
      description_long: "",
      duration_months: 3,
      delay_between_modules_days: 12,
      price: "",
      currency: "EUR",
      color: "#FF6B35",
      has_pcm_personalization: true,
      welcome_message: "",
    });
  };

  const handleCreateProgram = async (e) => {
    e.preventDefault();
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const response = await fetch(`${API}/admin/programs`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          ...formData,
          price: formData.price ? parseFloat(formData.price) : null,
        }),
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Programme créé avec succès");
        setIsCreateOpen(false);
        resetForm();
        fetchPrograms();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la création");
      }
    } catch (error) {
      toast.error("Erreur lors de la création");
    }
  };

  const handleUpdateProgram = async (e) => {
    e.preventDefault();
    if (!selectedProgram) return;

    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const response = await fetch(`${API}/admin/programs/${selectedProgram.program_id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          description_long: formData.description_long,
          duration_months: formData.duration_months,
          delay_between_modules_days: formData.delay_between_modules_days,
          price: formData.price ? parseFloat(formData.price) : null,
          currency: formData.currency,
          color: formData.color,
          has_pcm_personalization: formData.has_pcm_personalization,
          welcome_message: formData.welcome_message,
        }),
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Programme mis à jour");
        setIsEditOpen(false);
        setSelectedProgram(null);
        fetchPrograms();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la mise à jour");
      }
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleToggleActive = async (program) => {
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const response = await fetch(`${API}/admin/programs/${program.program_id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ is_active: !program.is_active }),
        credentials: "include",
      });

      if (response.ok) {
        toast.success(program.is_active ? "Programme désactivé" : "Programme activé");
        fetchPrograms();
      } else {
        toast.error("Erreur lors de la mise à jour");
      }
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleDeleteProgram = async (program) => {
    if (program.user_count > 0) {
      toast.error(`Impossible de supprimer : ${program.user_count} utilisateur(s) inscrit(s)`);
      return;
    }

    if (!confirm(`Supprimer le programme "${program.name}" ?`)) return;

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/admin/programs/${program.program_id}`, {
        method: "DELETE",
        headers,
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Programme supprimé");
        fetchPrograms();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la suppression");
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const openEditDialog = (program) => {
    setSelectedProgram(program);
    setFormData({
      name: program.name,
      slug: program.slug,
      description: program.description || "",
      description_long: program.description_long || "",
      duration_months: program.duration_months || 3,
      delay_between_modules_days: program.delay_between_modules_days || 12,
      price: program.price || "",
      currency: program.currency || "EUR",
      color: program.color || "#FF6B35",
      has_pcm_personalization: program.has_pcm_personalization !== false,
      welcome_message: program.welcome_message || "",
    });
    setIsEditOpen(true);
  };

  const ProgramForm = ({ onSubmit, submitLabel }) => (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Nom du programme *</Label>
          <Input
            value={formData.name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="Ex: FLOW, SPARK..."
            required
            data-testid="program-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Identifiant (slug)</Label>
          <Input
            value={formData.slug}
            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
            placeholder="flow, spark..."
            className="font-mono text-sm"
            data-testid="program-slug-input"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Description courte</Label>
        <Input
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Description brève du programme"
        />
      </div>

      <div className="space-y-2">
        <Label>Description longue</Label>
        <Textarea
          value={formData.description_long}
          onChange={(e) => setFormData({ ...formData, description_long: e.target.value })}
          placeholder="Description détaillée pour la page de présentation..."
          rows={3}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Durée (mois)</Label>
          <Input
            type="number"
            min="1"
            max="24"
            value={formData.duration_months}
            onChange={(e) => setFormData({ ...formData, duration_months: parseInt(e.target.value) || 3 })}
          />
        </div>
        <div className="space-y-2">
          <Label>Délai entre modules (jours)</Label>
          <Input
            type="number"
            min="0"
            max="90"
            value={formData.delay_between_modules_days}
            onChange={(e) => setFormData({ ...formData, delay_between_modules_days: parseInt(e.target.value) || 12 })}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Prix ({formData.currency})</Label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={formData.price}
            onChange={(e) => setFormData({ ...formData, price: e.target.value })}
            placeholder="Ex: 890"
          />
        </div>
        <div className="space-y-2">
          <Label>Couleur du programme</Label>
          <div className="flex items-center gap-2">
            <Input
              type="color"
              value={formData.color}
              onChange={(e) => setFormData({ ...formData, color: e.target.value })}
              className="w-12 h-10 p-1 cursor-pointer"
            />
            <Input
              value={formData.color}
              onChange={(e) => setFormData({ ...formData, color: e.target.value })}
              placeholder="#FF6B35"
              className="flex-1 font-mono text-sm"
            />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
        <div>
          <p className="font-medium text-sm">Personnalisation PCM</p>
          <p className="text-xs text-slate-500">Adapter le contenu au profil PCM de l'utilisateur</p>
        </div>
        <Switch
          checked={formData.has_pcm_personalization}
          onCheckedChange={(checked) => setFormData({ ...formData, has_pcm_personalization: checked })}
        />
      </div>

      <div className="space-y-2">
        <Label>Message de bienvenue</Label>
        <Textarea
          value={formData.welcome_message}
          onChange={(e) => setFormData({ ...formData, welcome_message: e.target.value })}
          placeholder="Message affiché lors de la première connexion..."
          rows={2}
        />
      </div>

      <Button
        type="submit"
        className="w-full bg-[#101E4D] hover:bg-[#0A1330]"
        data-testid="program-submit-btn"
      >
        {submitLabel}
      </Button>
    </form>
  );

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar />
        <main className="flex-1 ml-64 p-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#101E4D]" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <main className="flex-1 ml-64 p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-[#101E4D]">Programmes de formation</h1>
              <p className="text-slate-500 mt-1">
                Gérez vos différents parcours de coaching
              </p>
            </div>
            <div className="flex gap-3">
              {/* Migration Button - shown only if no programs exist */}
              {programs.length === 0 && (
                <Button
                  variant="outline"
                  onClick={runMigration}
                  disabled={migrationLoading}
                  className="border-amber-500 text-amber-600 hover:bg-amber-50"
                  data-testid="migration-btn"
                >
                  <Wand2 className="w-4 h-4 mr-2" />
                  {migrationLoading ? "Migration..." : "Initialiser FLOW"}
                </Button>
              )}
              <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
                <DialogTrigger asChild>
                  <Button
                    className="bg-[#FF6B35] hover:bg-[#E85D2A]"
                    onClick={() => { resetForm(); setIsCreateOpen(true); }}
                    data-testid="create-program-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nouveau programme
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Créer un programme</DialogTitle>
                  </DialogHeader>
                  <ProgramForm onSubmit={handleCreateProgram} submitLabel="Créer le programme" />
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Migration Result Alert */}
          {migrationResult && (
            <Alert className={`mb-6 ${migrationResult.success ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}>
              {migrationResult.success ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <AlertCircle className="h-4 w-4 text-red-600" />
              )}
              <AlertTitle className={migrationResult.success ? 'text-green-800' : 'text-red-800'}>
                {migrationResult.success ? 'Migration réussie !' : 'Erreur de migration'}
              </AlertTitle>
              <AlertDescription className={migrationResult.success ? 'text-green-700' : 'text-red-700'}>
                {migrationResult.message}
                {migrationResult.success && (
                  <ul className="mt-2 text-sm list-disc list-inside">
                    {migrationResult.program_created && <li>Programme FLOW créé</li>}
                    {migrationResult.modules_updated > 0 && <li>{migrationResult.modules_updated} modules assignés</li>}
                    {migrationResult.users_updated > 0 && <li>{migrationResult.users_updated} utilisateurs inscrits</li>}
                    {migrationResult.progress_updated > 0 && <li>{migrationResult.progress_updated} progressions mises à jour</li>}
                  </ul>
                )}
              </AlertDescription>
            </Alert>
          )}

          {/* Programs Grid */}
          {programs.length === 0 ? (
            <Card className="bg-white">
              <CardContent className="text-center py-12">
                <FolderOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-2">Aucun programme créé</p>
                <p className="text-slate-400 text-sm mb-6">
                  Cliquez sur "Initialiser FLOW" pour créer le programme par défaut avec vos modules existants,
                  ou créez un nouveau programme manuellement.
                </p>
                <div className="flex gap-3 justify-center">
                  <Button
                    variant="outline"
                    onClick={runMigration}
                    disabled={migrationLoading}
                    className="border-amber-500 text-amber-600 hover:bg-amber-50"
                  >
                    <Wand2 className="w-4 h-4 mr-2" />
                    {migrationLoading ? "Migration..." : "Initialiser FLOW"}
                  </Button>
                  <Button
                    onClick={() => { resetForm(); setIsCreateOpen(true); }}
                    className="bg-[#FF6B35] hover:bg-[#E85D2A]"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nouveau programme
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {programs.map((program) => (
                <Card
                  key={program.program_id}
                  className={`bg-white transition-all hover:shadow-lg ${!program.is_active ? 'opacity-60' : ''}`}
                  data-testid={`program-card-${program.program_id}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                          style={{ backgroundColor: program.color || '#FF6B35' }}
                        >
                          {program.name.charAt(0)}
                        </div>
                        <div>
                          <CardTitle className="text-lg">{program.name}</CardTitle>
                          <p className="text-xs text-slate-400 font-mono">/{program.slug}</p>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(program)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Modifier
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggleActive(program)}>
                            {program.is_active ? (
                              <>
                                <EyeOff className="w-4 h-4 mr-2" />
                                Désactiver
                              </>
                            ) : (
                              <>
                                <Eye className="w-4 h-4 mr-2" />
                                Activer
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => handleDeleteProgram(program)}
                            disabled={program.user_count > 0}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                      {program.description || "Aucune description"}
                    </p>

                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2 text-slate-500">
                        <Users className="w-4 h-4" />
                        <span>{program.user_count || 0} utilisateur{program.user_count !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-500">
                        <BookOpen className="w-4 h-4" />
                        <span>{program.module_count || 0} module{program.module_count !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-500">
                        <Calendar className="w-4 h-4" />
                        <span>{program.duration_months || 3} mois</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-500">
                        <Palette className="w-4 h-4" />
                        <span className="font-mono text-xs">{program.color}</span>
                      </div>
                    </div>

                    {program.price && (
                      <div className="mt-4 pt-3 border-t border-slate-100">
                        <p className="text-lg font-bold text-[#101E4D]">
                          {program.price} {program.currency}
                        </p>
                      </div>
                    )}

                    {!program.is_active && (
                      <div className="mt-3 px-3 py-2 bg-slate-100 rounded-lg text-center">
                        <span className="text-xs text-slate-500 font-medium">Programme désactivé</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Edit Dialog */}
          <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Modifier le programme</DialogTitle>
              </DialogHeader>
              <ProgramForm onSubmit={handleUpdateProgram} submitLabel="Enregistrer les modifications" />
            </DialogContent>
          </Dialog>
        </div>
      </main>
    </div>
  );
};

export default AdminPrograms;
